/**
 * Mention Parser - Convert Functions
 *
 * Functions for converting legacy mentions and extracting user IDs.
 */

import {
  STRUCTURED_MENTION_REGEX,
  LEGACY_MENTION_REGEX,
} from "./regex.js";
import { extractStructuredMentions } from "./extract.js";
import { resolveMentionsToUserIds } from "./resolve.js";

/**
 * Convert legacy @username mentions in text to structured @[Name](userId) tokens.
 *
 * This is a gradual migration function — it only converts legacy mentions that
 * successfully resolve to a valid user in Firestore. Mentions that can't be
 * resolved are left as-is (legacy format).
 *
 * @param {string} text - Text that may contain legacy @username mentions
 * @returns {Promise<string>} Text with resolvable legacy mentions converted to structured tokens
 */
export async function convertLegacyMentions(text) {
  if (!text || typeof text !== "string") return text;

  // Extract only legacy mentions (no structured tokens)
  const textWithoutStructured = text.replace(STRUCTURED_MENTION_REGEX, "");
  const legacyMatches = textWithoutStructured.match(LEGACY_MENTION_REGEX);
  if (!legacyMatches) return text;

  // Deduplicate and resolve
  const usernames = [
    ...new Set(legacyMatches.map((m) => m.substring(1).toLowerCase())),
  ];
  const resolved = await resolveMentionsToUserIds(usernames);

  // Replace each legacy mention with its structured token
  let result = text;
  for (const { username, userId } of resolved) {
    // Match the legacy mention (case-insensitive) at word boundary
    const escapedName = username.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const legacyRegex = new RegExp(`@${escapedName}\\b`, "gi");

    // For each match, check if it's NOT already inside a structured token
    const matches = [...result.matchAll(new RegExp(legacyRegex.source, "gi"))];
    for (const match of matches.reverse()) {
      // Check if this match is inside a structured token by looking at surrounding context
      const beforeMatch = result.substring(0, match.index);
      const openBracket = beforeMatch.lastIndexOf("@[");
      const closeParen = beforeMatch.lastIndexOf("](");
      // If there's an unclosed @[ before this match, skip (it's inside a token)
      if (
        openBracket !== -1 &&
        (closeParen === -1 || closeParen < openBracket)
      ) {
        continue;
      }
      // Replace only the last match (reverse order) to avoid index shifting issues
      const replacement = `@[${username}](${userId})`;
      result =
        result.substring(0, match.index) +
        replacement +
        result.substring(match.index + match[0].length);
      break; // Replace one at a time to handle multiple mentions with same username
    }
  }

  return result;
}

/**
 * Compute the set of userIds mentioned in text.
 * Structured tokens: extracted directly. Legacy: resolved via Firestore.
 *
 * @param {string} text
 * @returns {Promise<Set<string>>} Set of mentioned userIds
 */
export async function getMentionedUserIds(text) {
  const userIds = new Set();
  if (!text || typeof text !== "string") return userIds;

  // Structured tokens
  const structured = extractStructuredMentions(text);
  for (const m of structured) userIds.add(m.userId);

  // Legacy
  const textWithoutStructured = text.replace(STRUCTURED_MENTION_REGEX, "");
  const legacyMatches = textWithoutStructured.match(LEGACY_MENTION_REGEX);
  if (legacyMatches) {
    const usernames = [
      ...new Set(legacyMatches.map((m) => m.substring(1).toLowerCase())),
    ];
    const resolved = await resolveMentionsToUserIds(usernames);
    for (const r of resolved) userIds.add(r.userId);
  }

  return userIds;
}
